import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

import { Loading, Alert, Separator } from '../../components';

// Meta App configured for WhatsApp Cloud API Coexistence.
// App ID and Configuration ID are non-secret (they ship in the client-side
// SDK call by design). The App Secret stays server-side only.
const META_APP_ID = '1078600144350258';
const META_CONFIG_ID = '1892271644768539';
const META_GRAPH_VERSION = 'v20.0';
const FB_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';

function loadFacebookSdk() {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }
    const existing = document.getElementById('facebook-jssdk');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.FB));
      existing.addEventListener('error', reject);
      return;
    }
    window.fbAsyncInit = () => {
      window.FB.init({
        appId: META_APP_ID,
        cookie: true,
        xfbml: true,
        version: META_GRAPH_VERSION,
      });
      resolve(window.FB);
    };
    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.src = FB_SDK_URL;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

// Pull waba_id + phone_number_id out of an Embedded Signup postMessage
// payload. Meta uses two naming conventions depending on the signup variant:
//   - standard: { waba_id, phone_number_id }
//   - coexistence: { whatsapp_business_account_id, phone_number_id }
// Accept both so we don't silently drop the binding when Meta changes payload shape.
function extractSignupIds(data) {
  const wabaId = data?.waba_id || data?.whatsapp_business_account_id || null;
  const phoneNumberId = data?.phone_number_id || null;
  const businessId = data?.business_id || data?.business_portfolio_id || null;
  return { wabaId, phoneNumberId, businessId };
}

export function WhatsAppCoexistenceSettings() {
  const [bindings, setBindings] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const sdkLoadedRef = useRef(false);
  const signupPayloadRef = useRef({});

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bindingsRes, storesRes] = await Promise.all([
        axios.get('/api/admin/whatsapp/coexistence'),
        axios.get('/api/admin/stores/branding'),
      ]);
      setBindings(bindingsRes.data?.items || []);
      const storeItems = storesRes.data?.items || [];
      setStores(storeItems);
      if (!selectedStoreId && storeItems.length > 0) {
        const master = storeItems.find((s) => s.is_master_admin_store);
        setSelectedStoreId((master || storeItems[0]).id);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [selectedStoreId]);

  useEffect(() => {
    refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadFacebookSdk()
      .then(() => { sdkLoadedRef.current = true; })
      .catch(() => setError('Failed to load Facebook SDK'));
  }, []);

  useEffect(() => {
    function onMessage(ev) {
      if (typeof ev.origin !== 'string') return;
      if (!/^https:\/\/(www\.)?facebook\.com$/.test(ev.origin)) return;
      let data;
      try {
        data = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data;
      } catch (_) {
        return;
      }
      if (data?.type !== 'WA_EMBEDDED_SIGNUP') return;

      if (data.event === 'FINISH' || data.event === 'FINISH_ONLY_WABA') {
        const ids = extractSignupIds(data.data || {});
        signupPayloadRef.current = ids;
      } else if (data.event === 'CANCEL') {
        signupPayloadRef.current = {};
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const connect = () => {
    setError(null);
    setInfo(null);
    if (!window.FB) {
      setError('Facebook SDK not yet loaded');
      return;
    }
    if (!selectedStoreId) {
      setError('Pick a store first');
      return;
    }
    signupPayloadRef.current = {};
    setConnecting(true);

    window.FB.login(
      async (response) => {
        try {
          const code = response?.authResponse?.code;
          if (!code) {
            setConnecting(false);
            if (response?.status !== 'unknown') {
              setError('Signup cancelled or no auth code returned');
            }
            return;
          }
          // postMessage with WABA + phone IDs may arrive slightly after the
          // FB.login callback. Give it a short grace period before posting.
          const start = Date.now();
          while (
            (!signupPayloadRef.current.wabaId || !signupPayloadRef.current.phoneNumberId)
            && Date.now() - start < 3000
          ) {
            await new Promise((r) => setTimeout(r, 100));
          }
          const { wabaId, phoneNumberId, businessId } = signupPayloadRef.current;
          if (!wabaId || !phoneNumberId) {
            setConnecting(false);
            setError('Embedded Signup did not return WABA / phone IDs. Try again.');
            return;
          }

          const callbackRes = await axios.post(
            `/api/admin/whatsapp/coexistence/${selectedStoreId}/callback`,
            {
              code,
              waba_id: wabaId,
              whatsapp_business_account_id: wabaId,
              phone_number_id: phoneNumberId,
              business_id: businessId,
            },
          );
          setInfo(
            `Connected ${callbackRes.data?.binding?.display_phone_number || phoneNumberId}`
            + ` to ${callbackRes.data?.binding?.store_name || `store ${selectedStoreId}`}.`,
          );
          await refresh();
        } catch (e) {
          setError(e?.response?.data?.error || e.message || 'Connection failed');
        } finally {
          setConnecting(false);
        }
      },
      {
        config_id: META_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          featureType: 'whatsapp_business_app_onboarding',
        },
      },
    );
  };

  const disconnect = async (storeId) => {
    if (!window.confirm('Disconnect this store from WhatsApp Coexistence?')) return;
    setError(null);
    try {
      await axios.delete(`/api/admin/whatsapp/coexistence/${storeId}`);
      setInfo('Disconnected.');
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Disconnect failed');
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="wa-coexistence">
      <h2>WhatsApp Coexistence</h2>
      <p>
        Bind a WhatsApp Business number to a Retail Circle store via Meta&apos;s
        Embedded Signup. The number stays live on the iPhone WhatsApp Business
        app while Meta also delivers messages to Retail Circle&apos;s webhook.
      </p>

      {error && <Alert type="error">{error}</Alert>}
      {info && <Alert type="success">{info}</Alert>}

      <Separator />

      <h3>Connect a store</h3>
      <div className="wa-coexistence-connect">
        <label htmlFor="wa-coexistence-store">Store</label>
        <select
          id="wa-coexistence-store"
          value={selectedStoreId || ''}
          onChange={(e) => setSelectedStoreId(Number(e.target.value))}
        >
          {stores.map((s) => (
            <option key={s.id} value={s.id}>
              {s.display_name || s.name}
              {s.is_master_admin_store ? ' (RC platform)' : ''}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={connect}
          disabled={connecting || !selectedStoreId}
        >
          {connecting ? 'Opening Meta signup…' : 'Connect WhatsApp'}
        </button>
      </div>

      <Separator />

      <h3>Current bindings</h3>
      {bindings.length === 0 && <p>No stores connected yet.</p>}
      {bindings.length > 0 && (
        <table className="wa-coexistence-table">
          <thead>
            <tr>
              <th>Store</th>
              <th>Phone number</th>
              <th>WABA</th>
              <th>Status</th>
              <th>Connected</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {bindings.map((b) => (
              <tr key={b.store_id}>
                <td>{b.store_name}</td>
                <td>{b.display_phone_number || b.phone_number_id || '—'}</td>
                <td>{b.waba_id || '—'}</td>
                <td>{b.coexistence_enabled ? 'Active' : 'Disconnected'}</td>
                <td>{b.connected_at ? new Date(b.connected_at).toLocaleString() : '—'}</td>
                <td>
                  {b.coexistence_enabled && (
                    <button type="button" onClick={() => disconnect(b.store_id)}>
                      Disconnect
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
